import React, { useCallback, useEffect, useMemo, useState } from "react";
import ELK from "elkjs/lib/elk.bundled.js";
import ReactFlow, {
  Background,
  Controls,
  Handle,
  MarkerType,
  Position,
  ReactFlowProvider,
  useReactFlow,
  useUpdateNodeInternals,
} from "reactflow";
import "reactflow/dist/style.css";
import { toast } from "../../utils/toast";
import VirtualList from "../ui/VirtualList";
import { compareNames, jobKey, jobMatches, jobName, jobStaff } from "./hierarchyModel";

const NODE_WIDTH = 320;
const NODE_HEIGHT = 90;
const ROOT_WIDTH = 320;
const ROOT_HEIGHT = 96;

// Job groups have a bounded height however many jobs a function holds: a
// header, a filter for long lists, and a scrollable window of rows of which
// only the visible ones are mounted.
const JOB_ITEM_HEIGHT = 52;
const JOB_GROUP_GAP = 8;
const JOB_ROW = JOB_ITEM_HEIGHT + JOB_GROUP_GAP;
const JOB_GROUP_VISIBLE = 6;
const JOB_GROUP_CHROME = 4 + 12 + 4; // border + top/bottom padding
const JOB_GROUP_HEADER = 30;
const JOB_GROUP_FILTER = 42;
const JOB_FILTER_MIN = 12;

const getJobGroupHeight = (jobCount) =>
  JOB_GROUP_CHROME +
  JOB_GROUP_HEADER +
  (jobCount > JOB_FILTER_MIN ? JOB_GROUP_FILTER : 0) +
  Math.max(1, Math.min(jobCount, JOB_GROUP_VISIBLE)) * JOB_ROW;

const ELK_INSTANCE = new ELK();

// ELK layouts (as promises of node positions), remembered per loaded function
// list. Re-opening a function, switching views or flipping orientation back
// reuses a layout, and identical requests made while one runs share it.
const layoutCache = new WeakMap();
const LAYOUT_CACHE_LIMIT = 40;

function layoutFor(funcs, key, run) {
  let entries = layoutCache.get(funcs);
  if (!entries) {
    entries = new Map();
    layoutCache.set(funcs, entries);
  }
  let pending = entries.get(key);
  if (!pending) {
    pending = run().catch((e) => {
      entries.delete(key);
      throw e;
    });
    entries.set(key, pending);
    if (entries.size > LAYOUT_CACHE_LIMIT) entries.delete(entries.keys().next().value);
  }
  return pending;
}

const getHandleConfig = (orientation) =>
  orientation === "horizontal" ?
    {
      source: "right-source",
      target: "left-target",
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
    }
  : {
      source: "bottom-source",
      target: "top-target",
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
    };

const getNodePositions = (orientation) => {
  const handles = getHandleConfig(orientation);
  return {
    sourcePosition: handles.sourcePosition,
    targetPosition: handles.targetPosition,
  };
};

const applyEdgeOrientation = (edges, orientation) => {
  const handles = getHandleConfig(orientation);
  return edges.map((edge) => ({
    ...edge,
    sourcePosition: handles.sourcePosition,
    targetPosition: handles.targetPosition,
  }));
};

// React Flow re-measures handles for every node in one batched call
// (FlowInternalsSync), so nodes don't each trigger their own update.
const HierarchyNode = ({ data }) => {
  const handles = getHandleConfig(data.orientation);
  const sourceHandles = Array.isArray(data.sourceHandles) ?
    data.sourceHandles
  : [];
  const targetHandles = Array.isArray(data.targetHandles) ?
    data.targetHandles
  : [];
  const isHorizontal = data.orientation === "horizontal";

  return (
    <div className="hierarchy-flow-node">
      {data.showTarget !== false && (
        targetHandles.length > 0 ?
          targetHandles.map((handle) => (
            <Handle
              key={handle.id}
              type="target"
              id={handle.id}
              position={handles.targetPosition}
              className="hierarchy-handle"
              style={
                isHorizontal ?
                  { top: `${handle.percent}%`, left: -4 }
                : { left: `${handle.percent}%`, top: -4 }
              }
            />
          ))
        : <Handle
            type="target"
            id={handles.target}
            position={handles.targetPosition}
            className="hierarchy-handle"
          />
      )}
      {data.label}
      {data.showSource !== false && (
        sourceHandles.length > 0 ?
          sourceHandles.map((handle) => (
            <Handle
              key={handle.id}
              type="source"
              id={handle.id}
              position={handles.sourcePosition}
              className="hierarchy-handle"
              style={
                data.orientation === "horizontal" ?
                  { top: `${handle.percent}%`, right: -4 }
                : { left: `${handle.percent}%`, bottom: -4 }
              }
            />
          ))
        : <Handle
            type="source"
            id={handles.source}
            position={handles.sourcePosition}
            className="hierarchy-handle"
          />
      )}
    </div>
  );
};

const nodeTypes = { hierarchyNode: HierarchyNode };

// The jobs of an expanded function.
const JobGroup = ({ jobs }) => {
  const [filter, setFilter] = useState("");
  const q = filter.trim().toLowerCase();
  const shown = useMemo(() => (q ? jobs.filter((j) => jobMatches(j, q)) : jobs), [jobs, q]);
  const staff = useMemo(() => jobs.reduce((n, j) => n + jobStaff(j), 0), [jobs]);
  const rows = Math.max(1, Math.min(jobs.length, JOB_GROUP_VISIBLE));
  const scrollable = shown.length > JOB_GROUP_VISIBLE;

  return (
    <div className="hierarchy-job-group" style={{ height: getJobGroupHeight(jobs.length) }}>
      <div className="hierarchy-job-group-head">
        <span>
          <i className="fa fa-briefcase" aria-hidden="true" />{" "}
          {q ?
            `${shown.length.toLocaleString()} of ${jobs.length.toLocaleString()} jobs`
          : `${jobs.length.toLocaleString()} job${jobs.length === 1 ? "" : "s"}`}
        </span>
        {staff > 0 && (
          <span className="hierarchy-job-group-staff" title="Total staff">
            <i className="fa fa-users" aria-hidden="true" /> {staff.toLocaleString()}
          </span>
        )}
      </div>
      {jobs.length > JOB_FILTER_MIN && (
        <input
          type="text"
          className="hierarchy-job-group-filter nodrag nopan"
          value={filter}
          placeholder={`Filter ${jobs.length.toLocaleString()} jobs…`}
          aria-label="Filter jobs"
          onChange={(e) => setFilter(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
      )}
      {shown.length === 0 ?
        <div className="hierarchy-job-group-empty">No jobs match “{filter}”.</div>
      : <VirtualList
          className={`hierarchy-job-group-list${scrollable ? " nowheel nodrag nopan" : ""}`}
          items={shown}
          itemHeight={JOB_ROW}
          maxHeight={rows * JOB_ROW}
          overscan={1}
          resetKey={q}
          renderItem={(j, i, style) => (
            <div key={jobKey(j, i)} style={style}>
              <div className="hierarchy-job-group-item" title={jobName(j)}>
                <div className="job-group-item-icon">
                  <i className="fa fa-briefcase" aria-hidden="true" />
                </div>
                <div className="job-group-item-title">{jobName(j)}</div>
                {jobStaff(j) > 0 && (
                  <div className="job-group-item-staff" title="Staff count">
                    <i className="fa fa-users" aria-hidden="true" />
                    <span>{jobStaff(j)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        />
      }
    </div>
  );
};

const createHierarchyEdge = ({
  id,
  source,
  target,
  orientation,
  color,
  dashed = false,
  sourceHandle,
}) => {
  const handles = getHandleConfig(orientation);
  const isJobEdge = Boolean(dashed);
  return {
    id,
    source,
    target,
    sourceHandle: sourceHandle || handles.source,
    targetHandle: handles.target,
    type: isJobEdge ? "smoothstep" : "step",
    sourcePosition: handles.sourcePosition,
    targetPosition: handles.targetPosition,
    animated: false,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 18,
      height: 18,
      color,
    },
    style: {
      stroke: color,
      strokeWidth: 2,
      ...(dashed ? { strokeDasharray: "6,4" } : {}),
    },
    pathOptions: isJobEdge ?
      { borderRadius: 8, offset: 8 }
    : { borderRadius: 12, offset: 10 },
  };
};

const alignJobGroupConnections = (nodes, edges, orientation) => {
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const targetHandlesByNode = new Map();
  const isHorizontal = orientation === "horizontal";

  const alignedEdges = edges.map((edge) => {
    if (!edge.target?.startsWith("job-group-")) return edge;

    const sourceNode = nodeById.get(edge.source);
    const targetNode = nodeById.get(edge.target);
    if (!sourceNode || !targetNode) return edge;

    const targetHandleId = `${edge.target}-in-aligned-${isHorizontal ? "h" : "v"}`;
    let percent = 50;

    if (isHorizontal) {
      const sourceH = sourceNode.height || NODE_HEIGHT;
      const targetH = targetNode.height || NODE_HEIGHT;
      const sourceCenterY = sourceNode.position.y + sourceH / 2;
      percent = Math.min(
        95,
        Math.max(5, ((sourceCenterY - targetNode.position.y) / targetH) * 100),
      );
    } else {
      const sourceW = sourceNode.width || NODE_WIDTH;
      const targetW = targetNode.width || NODE_WIDTH;
      const sourceCenterX = sourceNode.position.x + sourceW / 2;
      percent = Math.min(
        95,
        Math.max(5, ((sourceCenterX - targetNode.position.x) / targetW) * 100),
      );
    }

    targetHandlesByNode.set(edge.target, [{ id: targetHandleId, percent }]);

    const handles = getHandleConfig(orientation);
    return {
      ...edge,
      targetHandle: targetHandleId,
      type: "smoothstep",
      sourcePosition: handles.sourcePosition,
      targetPosition: handles.targetPosition,
      pathOptions: { borderRadius: 8, offset: 8 },
    };
  });

  const alignedNodes = nodes.map((node) => {
    const targetHandles = targetHandlesByNode.get(node.id);
    if (!targetHandles) return node;
    return {
      ...node,
      data: { ...node.data, targetHandles },
    };
  });

  return {
    nodes: alignedNodes,
    edges: applyEdgeOrientation(alignedEdges, orientation),
  };
};

// Sorting and adjacency for a loaded function list — independent of which
// functions are expanded, so it is built once per company.
function buildGraphStructure(funcs) {
  const sortedFuncs = [...(funcs || [])].sort((a, b) => {
    const parentCmp = Number(a.parent_id || 0) - Number(b.parent_id || 0);
    if (parentCmp !== 0) return parentCmp;
    const nameCmp = compareNames(a.name, b.name);
    if (nameCmp !== 0) return nameCmp;
    return Number(a.function_id || 0) - Number(b.function_id || 0);
  });

  const byId = new Map(sortedFuncs.map((f) => [Number(f.function_id), f]));
  const childrenOf = new Map();
  sortedFuncs.forEach((f) => {
    const pid = f.parent_id ? Number(f.parent_id) : null;
    if (pid) {
      if (!childrenOf.has(pid)) childrenOf.set(pid, []);
      childrenOf.get(pid).push(f);
    }
  });

  // Jobs sorted by name, computed the first time a function is opened.
  const sortedJobs = new Map();
  const jobsOf = (fnId) => {
    let jobs = sortedJobs.get(fnId);
    if (!jobs) {
      const f = byId.get(fnId);
      jobs = (Array.isArray(f?.jobs) ? [...f.jobs] : []).sort((a, b) => {
        const nameCmp = compareNames(a?.name, b?.name);
        if (nameCmp !== 0) return nameCmp;
        return Number(a?.job_id || a?.id || 0) - Number(b?.job_id || b?.id || 0);
      });
      sortedJobs.set(fnId, jobs);
    }
    return jobs;
  };

  return { sortedFuncs, byId, childrenOf, jobsOf };
}

// Nodes and edges (without positions) for the current expansion.
function buildRawGraph({ structure, expanded, layoutOrientation, selectedCompany, selectedCompanyId }) {
  if (!selectedCompanyId) return { nodes: [], edges: [] };
  const { sortedFuncs, byId, childrenOf, jobsOf } = structure;

  const createdNodes = [];
  const edgeDefs = [];
  const rootId = `company-${selectedCompanyId}`;

  const companyLabel = (
    <div className="hierarchy-node company-node">
      <div className="node-icon">
        <i className="fa fa-building" aria-hidden="true" />
      </div>
      <div className="node-content">
        <div className="node-title">
          {selectedCompany ? selectedCompany.name || "Company" : "Company"}
        </div>
        <div className="node-subtitle">Organization Root</div>
      </div>
    </div>
  );

  const nodePositions = getNodePositions(layoutOrientation);
  const handleSuffix = layoutOrientation === "horizontal" ? "h" : "v";

  createdNodes.push({
    id: rootId,
    position: { x: 0, y: 0 },
    type: "hierarchyNode",
    width: ROOT_WIDTH,
    height: ROOT_HEIGHT,
    ...nodePositions,
    data: {
      label: companyLabel,
      orientation: layoutOrientation,
      showTarget: false,
      showSource: true,
    },
    className: "hierarchy-root-node",
  });

  sortedFuncs.forEach((f) => {
    const fnId = `function-${f.function_id}`;
    const isParent = childrenOf.has(Number(f.function_id));
    const hasJobs = Array.isArray(f.jobs) && f.jobs.length > 0;
    const isExpanded = expanded.has(Number(f.function_id));
    const iconClass = isParent ? "fa fa-sitemap" : "fa fa-cube";

    const label = (
      <div
        className={`hierarchy-node function-node ${
          isParent ? "has-children" : ""
        } ${isExpanded ? "expanded" : ""}`}
      >
        <div className="node-icon">
          <i className={iconClass} aria-hidden="true" />
        </div>
        <div className="node-content">
          <div className="node-title">{f.name || "Function"}</div>
          {hasJobs && (
            <div className="node-badge">
              {f.jobs.length.toLocaleString()} job{f.jobs.length > 1 ? "s" : ""}
            </div>
          )}
        </div>
        {hasJobs && (
          <div className="node-expand-indicator">
            <i className={`fa fa-chevron-${isExpanded ? "up" : "down"}`} />
          </div>
        )}
      </div>
    );

    createdNodes.push({
      id: fnId,
      position: { x: 0, y: 0 },
      type: "hierarchyNode",
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
      ...nodePositions,
      data: {
        label,
        orientation: layoutOrientation,
        showTarget: true,
        showSource: true,
      },
      className: `hierarchy-function-node ${isExpanded ? "expanded" : ""}`,
    });

    if (f.parent_id && byId.has(Number(f.parent_id))) {
      const parentIdNum = Number(f.parent_id);
      const parentFn = byId.get(parentIdNum);
      const parentHasJobs = Array.isArray(parentFn?.jobs) && parentFn.jobs.length > 0;
      const parentIsExpanded = expanded.has(parentIdNum);

      // When parent function is expanded and has jobs, route the child function's edge
      // from the parent's job-group node. This inserts the job container into Rank L+1
      // and pushes child sub-functions to Rank L+2 in ELK's layered layout engine.
      const parentSourceId =
        parentIsExpanded && parentHasJobs
          ? `job-group-${parentIdNum}`
          : `function-${parentIdNum}`;

      edgeDefs.push({
        id: `${parentSourceId}-${fnId}`,
        source: parentSourceId,
        target: fnId,
        color: "#4F46E5",
        dashed: false,
      });
    } else {
      edgeDefs.push({
        id: `${rootId}-${fnId}`,
        source: rootId,
        target: fnId,
        color: "#4F46E5",
        dashed: false,
      });
    }
  });

  sortedFuncs.forEach((f) => {
    const fnIdNum = Number(f.function_id);
    if (!expanded.has(fnIdNum) || !(Array.isArray(f.jobs) && f.jobs.length > 0)) return;
    const jobs = jobsOf(fnIdNum);

    const jobGroupId = `job-group-${f.function_id}`;
    const hasChildSubFuncs = (childrenOf.get(fnIdNum) || []).length > 0;

    createdNodes.push({
      id: jobGroupId,
      position: { x: 0, y: 0 },
      type: "hierarchyNode",
      width: NODE_WIDTH,
      height: getJobGroupHeight(jobs.length),
      ...nodePositions,
      data: {
        label: <JobGroup jobs={jobs} />,
        orientation: layoutOrientation,
        showTarget: true,
        showSource: hasChildSubFuncs,
      },
      className: "hierarchy-job-group-node",
    });

    edgeDefs.push({
      id: `function-${f.function_id}-${jobGroupId}`,
      source: `function-${f.function_id}`,
      target: jobGroupId,
      color: "#10B981",
      dashed: true,
    });
  });

  const orderedEdgeDefs = edgeDefs.sort((a, b) => {
    const sourceCmp = compareNames(a.source, b.source);
    if (sourceCmp !== 0) return sourceCmp;
    const targetCmp = compareNames(a.target, b.target);
    if (targetCmp !== 0) return targetCmp;
    return compareNames(a.id, b.id);
  });

  const outgoingBySource = new Map();
  orderedEdgeDefs.forEach((edge) => {
    if (!outgoingBySource.has(edge.source)) outgoingBySource.set(edge.source, []);
    outgoingBySource.get(edge.source).push(edge);
  });

  const sourceHandlesByNode = new Map();
  const handleByEdgeId = new Map();
  outgoingBySource.forEach((edgesForSource, sourceId) => {
    const functionEdges = edgesForSource.filter(
      (edge) => !String(edge.target).startsWith("job-group-"),
    );
    const jobEdges = edgesForSource.filter((edge) =>
      String(edge.target).startsWith("job-group-"),
    );
    const handles = [];

    // A single source handle for ALL child-function edges, so exactly one
    // line leaves the node (from its center) and then branches to its
    // children — regardless of how many children there are.
    if (functionEdges.length > 0) {
      const handleId = `${sourceId}-out-${handleSuffix}`;
      handles.push({ id: handleId, percent: 50 });
      functionEdges.forEach((edge) => handleByEdgeId.set(edge.id, handleId));
    }

    // One shared handle for the job-group edge(s).
    if (jobEdges.length > 0) {
      const jobHandleId = `${sourceId}-job-out-${handleSuffix}`;
      handles.push({ id: jobHandleId, percent: 50 });
      jobEdges.forEach((edge) => handleByEdgeId.set(edge.id, jobHandleId));
    }

    sourceHandlesByNode.set(sourceId, handles);
  });

  const nodesWithPorts = createdNodes.map((node) => ({
    ...node,
    ...nodePositions,
    data: {
      ...node.data,
      sourceHandles: sourceHandlesByNode.get(node.id) || [],
    },
  }));

  const createdEdges = orderedEdgeDefs.map((edge) =>
    createHierarchyEdge({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      orientation: layoutOrientation,
      color: edge.color,
      dashed: edge.dashed,
      sourceHandle: handleByEdgeId.get(edge.id),
    }),
  );

  return { nodes: nodesWithPorts, edges: createdEdges };
}

// The ELK input for a raw graph.
function buildElkGraph(rawGraph, layoutOrientation) {
  return {
    id: "root",
    layoutOptions: {
      "elk.algorithm": "layered",
      "elk.direction":
        layoutOrientation === "horizontal" ? "RIGHT" : "DOWN",
      "elk.edgeRouting": "ORTHOGONAL",
      "elk.considerModelOrder": "NODES_AND_EDGES",
      "elk.layered.crossingMinimization.forceNodeModelOrder": "true",
      "elk.layered.nodePlacement.bk.fixedAlignment": "BALANCED",
      "elk.layered.spacing.nodeNodeBetweenLayers": "110",
      "elk.spacing.nodeNode": "64",
      "elk.padding": "[top=40,left=40,bottom=40,right=40]",
      "elk.layered.crossingMinimization.strategy": "LAYER_SWEEP",
    },
    children: rawGraph.nodes.map((node) => {
      const sourceHandles = Array.isArray(node.data?.sourceHandles) ?
        node.data.sourceHandles
      : [];
      const isJobGroup = String(node.id).startsWith("job-group-");
      const targetPorts =
        isJobGroup ?
          [{
            id: `${node.id}-in-elk`,
            width: 1,
            height: 1,
            layoutOptions: {
              "org.eclipse.elk.port.side":
                layoutOrientation === "horizontal" ? "WEST" : "NORTH",
            },
          }]
        : [];

      return {
        id: node.id,
        width: node.width || NODE_WIDTH,
        height: node.height || NODE_HEIGHT,
        layoutOptions: {
          "elk.portConstraints": "FIXED_ORDER",
        },
        ports: [
          ...targetPorts,
          ...sourceHandles.map((handle, idx) => ({
            id: handle.id,
            width: 1,
            height: 1,
            layoutOptions: {
              "org.eclipse.elk.port.index": String(idx),
              "org.eclipse.elk.port.side":
                layoutOrientation === "horizontal" ? "EAST" : "SOUTH",
            },
          })),
        ],
      };
    }),
    edges: rawGraph.edges.map((edge) => ({
      id: edge.id,
      sources: [edge.sourceHandle || edge.source],
      targets: String(edge.target).startsWith("job-group-") ?
        [`${edge.target}-in-elk`]
      : [edge.target],
    })),
  };
}

const FlowInternalsSync = ({ nodes, orientation }) => {
  const updateNodeInternals = useUpdateNodeInternals();

  useEffect(() => {
    if (!nodes.length) return undefined;
    // React Flow renders new nodes after this commit; measure them just after.
    const timer = setTimeout(() => {
      updateNodeInternals(nodes.map((node) => node.id));
    }, 50);
    return () => clearTimeout(timer);
  }, [nodes, orientation, updateNodeInternals]);

  return null;
};

const HierarchyFlowInner = ({
  nodes,
  edges,
  onNodeClick,
  layoutOrientation,
  fitViewRef,
  showLegend,
  onToggleLegend,
  fitKey,
}) => {
  const { fitView } = useReactFlow();

  const handleFitView = useCallback(() => {
    fitView({ padding: 0.2, duration: 300 });
  }, [fitView]);

  useEffect(() => {
    if (fitViewRef) {
      fitViewRef.current = handleFitView;
      return () => {
        fitViewRef.current = null;
      };
    }
  }, [fitViewRef, handleFitView]);

  // Auto-fit only when the diagram identity changes (company / orientation) —
  // NOT on every expand/collapse, so opening a node doesn't zoom the viewport.
  const lastFitKeyRef = React.useRef(null);
  useEffect(() => {
    if (!nodes.length) return undefined;
    if (lastFitKeyRef.current === fitKey) return undefined;
    lastFitKeyRef.current = fitKey;
    const timer = setTimeout(() => {
      fitView({ padding: 0.2, duration: 300 });
    }, 100);
    return () => clearTimeout(timer);
  }, [nodes, fitKey, fitView]);

  return (
  <div className="hierarchy-flow-container">
    <ReactFlow
      key={layoutOrientation}
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      onNodeClick={onNodeClick}
      minZoom={0.1}
      maxZoom={2}
      defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
      attributionPosition="bottom-left"
    >
      <Background color="#E5E7EB" gap={20} size={1} />
      <Controls showInteractive={false} />
      <FlowInternalsSync nodes={nodes} orientation={layoutOrientation} />
    </ReactFlow>

    {showLegend ?
      <div className="hierarchy-legend">
        <div className="legend-header">
          <div className="legend-title">Legend</div>
          <button
            type="button"
            className="legend-toggle-btn"
            onClick={onToggleLegend}
            title="Hide legend"
            aria-label="Hide legend"
          >
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        </div>
        <div className="legend-items">
          <div className="legend-item">
            <div className="legend-icon company-legend">
              <i className="fa fa-building" />
            </div>
            <span>Company</span>
          </div>
          <div className="legend-item">
            <div className="legend-icon parent-function-legend">
              <i className="fa fa-sitemap" />
            </div>
            <span>Parent Function</span>
          </div>
          <div className="legend-item">
            <div className="legend-icon child-function-legend">
              <i className="fa fa-cube" />
            </div>
            <span>Child Function</span>
          </div>
          <div className="legend-item">
            <div className="legend-icon job-legend">
              <i className="fa fa-briefcase" />
            </div>
            <span>Jobs (grouped)</span>
          </div>
        </div>
        <div className="legend-divider" />
        <div className="legend-note">
          <i className="fa fa-info-circle" />
          <span>Click functions to expand jobs</span>
        </div>
      </div>
    : <button
        type="button"
        className="hierarchy-legend-show-btn"
        onClick={onToggleLegend}
        title="Show legend"
        aria-label="Show legend"
      >
        <i className="fa fa-info-circle" aria-hidden="true" />
        Legend
      </button>}
  </div>
  );
};

// A React Flow spatial org chart laid out with ELK. Owns its own graph-local
// state (orientation, expanded functions, legend visibility, computed layout).
const HierarchyGraphView = ({ funcs, selectedCompany, selectedCompanyId }) => {
  const [expanded, setExpanded] = useState(() => new Set());
  const [layoutOrientation, setLayoutOrientation] = useState("vertical");
  const fitViewRef = React.useRef(null);
  const [layoutedNodes, setLayoutedNodes] = useState([]);
  const [layoutedEdges, setLayoutedEdges] = useState([]);
  const [showLegend, setShowLegend] = useState(() => {
    try {
      return localStorage.getItem("hierarchyShowLegend") !== "0";
    } catch {
      return true;
    }
  });

  const toggleLegend = useCallback(() => {
    setShowLegend((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("hierarchyShowLegend", next ? "1" : "0");
      } catch {
        // ignore storage errors
      }
      return next;
    });
  }, []);

  // Reset expansion when the selected company changes.
  useEffect(() => {
    setExpanded(new Set());
  }, [selectedCompanyId]);

  const structure = useMemo(() => buildGraphStructure(funcs), [funcs]);

  const rawGraph = useMemo(
    () => buildRawGraph({ structure, expanded, layoutOrientation, selectedCompany, selectedCompanyId }),
    [structure, expanded, layoutOrientation, selectedCompany, selectedCompanyId],
  );

  const layoutKey = useMemo(
    () => `${layoutOrientation}|${[...expanded].sort((a, b) => a - b).join(",")}`,
    [expanded, layoutOrientation],
  );

  useEffect(() => {
    let cancelled = false;

    const apply = (positionById) => {
      const positionedNodes = rawGraph.nodes.map((node) => ({
        ...node,
        ...getNodePositions(layoutOrientation),
        position: positionById.get(node.id) || { x: 0, y: 0 },
        data: {
          ...node.data,
          orientation: layoutOrientation,
        },
      }));

      const { nodes: alignedNodes, edges: alignedEdges } =
        alignJobGroupConnections(
          positionedNodes,
          rawGraph.edges,
          layoutOrientation,
        );

      setLayoutedNodes(alignedNodes);
      setLayoutedEdges(alignedEdges);
    };

    const runLayout = async () => {
      if (!selectedCompanyId || rawGraph.nodes.length === 0) {
        setLayoutedNodes([]);
        setLayoutedEdges([]);
        return;
      }

      try {
        const elkGraph = buildElkGraph(rawGraph, layoutOrientation);
        const positionById = await layoutFor(funcs, layoutKey, async () => {
          const layoutResult = await ELK_INSTANCE.layout(elkGraph);
          return new Map(
            (layoutResult.children || []).map((node) => [
              node.id,
              { x: node.x || 0, y: node.y || 0 },
            ]),
          );
        });
        if (cancelled) return;
        apply(positionById);
      } catch (e) {
        if (cancelled) return;
        toast.error(e?.message || "Failed to compute hierarchy layout");
        const fallback = alignJobGroupConnections(
          rawGraph.nodes,
          rawGraph.edges,
          layoutOrientation,
        );
        setLayoutedNodes(fallback.nodes);
        setLayoutedEdges(fallback.edges);
      }
    };

    runLayout();

    return () => {
      cancelled = true;
    };
  }, [layoutOrientation, rawGraph, selectedCompanyId, funcs, layoutKey]);

  const functionsWithJobs = useMemo(
    () =>
      (funcs || []).filter(
        (f) => Array.isArray(f.jobs) && f.jobs.length > 0,
      ),
    [funcs],
  );

  const handleExpandAll = () => {
    const ids = functionsWithJobs.map((f) => Number(f.function_id));
    setExpanded(new Set(ids));
  };

  const handleCollapseAll = () => {
    setExpanded(new Set());
  };

  const onNodeClick = (_evt, node) => {
    if (typeof node?.id === "string" && node.id.startsWith("function-")) {
      const idStr = node.id.split("function-")[1];
      const idNum = Number(idStr);
      if (!Number.isFinite(idNum)) return;
      // Only functions that actually have jobs can be opened. Ignore clicks on
      // functions with no jobs to expand.
      const fn = structure.byId.get(idNum);
      const hasJobs = fn && Array.isArray(fn.jobs) && fn.jobs.length > 0;
      if (!hasJobs) return;
      setExpanded((prev) => {
        // If clicking the same function, toggle it off
        if (prev.has(idNum)) {
          return new Set();
        }
        // Otherwise, close all others and open only this one
        return new Set([idNum]);
      });
    }
  };

  return (
    <div className="hierarchy-graph-view">
      <div className="hierarchy-graph-toolbar">
        <div className="hierarchy-orientation-control">
          <span className="hierarchy-orientation-label">Layout Orientation</span>
          <div className="hierarchy-orientation-toggle">
            <button
              type="button"
              className={`hierarchy-orientation-btn ${
                layoutOrientation === "vertical" ? "active" : ""
              }`}
              onClick={() => setLayoutOrientation("vertical")}
              title="Vertical layout"
            >
              <i className="fa fa-sitemap" aria-hidden="true" />
              Vertical
            </button>
            <button
              type="button"
              className={`hierarchy-orientation-btn ${
                layoutOrientation === "horizontal" ? "active" : ""
              }`}
              onClick={() => setLayoutOrientation("horizontal")}
              title="Horizontal layout"
            >
              <i className="fa fa-share-alt" aria-hidden="true" />
              Horizontal
            </button>
          </div>
        </div>

        <button
          type="button"
          className={`secondary-btn sm hierarchy-toolbar-btn ${
            showLegend ? "active" : ""
          }`}
          onClick={toggleLegend}
          title={showLegend ? "Hide legend" : "Show legend"}
        >
          <i className="fa fa-info-circle" aria-hidden="true" />
          {showLegend ? "Hide Legend" : "Show Legend"}
        </button>

        <button
          type="button"
          className="secondary-btn sm hierarchy-toolbar-btn"
          onClick={() => fitViewRef.current?.()}
          title="Fit view"
        >
          <i className="fa fa-compress" aria-hidden="true" />
          Fit View
        </button>

        <button
          type="button"
          className="secondary-btn sm hierarchy-toolbar-btn"
          onClick={handleExpandAll}
          disabled={functionsWithJobs.length === 0}
          title="Expand all functions"
        >
          <i className="fa fa-expand" aria-hidden="true" />
          Expand All
        </button>

        <button
          type="button"
          className="secondary-btn sm hierarchy-toolbar-btn"
          onClick={handleCollapseAll}
          disabled={expanded.size === 0}
          title="Collapse all functions"
        >
          <i className="fa fa-angle-double-up" aria-hidden="true" />
          Collapse All
        </button>
      </div>

      <ReactFlowProvider>
        <HierarchyFlowInner
          nodes={layoutedNodes}
          edges={layoutedEdges}
          onNodeClick={onNodeClick}
          layoutOrientation={layoutOrientation}
          fitViewRef={fitViewRef}
          showLegend={showLegend}
          onToggleLegend={toggleLegend}
          fitKey={`${selectedCompanyId}-${layoutOrientation}`}
        />
      </ReactFlowProvider>
    </div>
  );
};

export default HierarchyGraphView;
